"use client";

import { getConvertedJpeg } from "api/get-converted-jpeg.api";
import classNames from "classnames";
import useScreenSize from "hoc/useScreenSize";
import WebpContext from "hooks-contexts/WebpContext";
import { useContext, useEffect, useState } from "react";
import { getAbsoluteImagePath } from "util/helper/getAbsoluteImagePath";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { getImageAlt } from "util/product/getImageAlt";
import MagnifyImage from "./magnify-image/MagnifyImage";
import styles from "./mobile-image-slider.module.scss";

function ProductImage(props) {
  const {
    imageObj,
    zoomImagePlacement,
    index,
    imageSize,
    imageTitle,
    isWebpImage,
    getProductImagePath,
    largeImage = false,
    smallImageClass = "",
    magnifiedContainerStyle = {},
    maxWidth,
    maxHeight,
  } = props;

  const relativeZoomSrc = isWebpImage
    ? getRelativeImagePath(`/img/ztm/${imageObj.zoomImg}`)
    : getRelativeImagePath(getProductImagePath(imageObj.zoomImg));
  const relativeImgSrc = isWebpImage
    ? getRelativeImagePath(`/img/${imageSize}/${imageObj.img}`)
    : getRelativeImagePath(getProductImagePath(imageObj.img));

  const [zoomSrc, setZoomSrc] = useState(relativeZoomSrc);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isMobile] = useScreenSize();
  const [convertedImageSrc, setConvertedImageSrc] = useState(relativeImgSrc);
  const { canUseWebp } = useContext(WebpContext);
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (zoomImagePlacement === "beside") setPortalEl(document.getElementById("portal"));
  }, [zoomImagePlacement]);

  useEffect(() => {
    if (!canUseWebp && relativeImgSrc.endsWith(".webp") && zoomSrc.endsWith(".webp")) {
      const absoluteSrc = getAbsoluteImagePath(relativeImgSrc);
      const absoluteZoomSrc = getAbsoluteImagePath(zoomSrc);
      getConvertedJpeg(absoluteSrc)
        .then((base64) => setConvertedImageSrc(`data:image/jpeg;base64,${base64}`))
        .catch(() => {});
      getConvertedJpeg(absoluteZoomSrc)
        .then((base64) => setZoomSrc(`data:image/jpeg;base64,${base64}`))
        .catch(() => {});
    }
  }, [canUseWebp, relativeImgSrc]);

  useEffect(() => {
    setZoomSrc(relativeZoomSrc);
  }, [relativeZoomSrc]);

  function onImageLoad(element: HTMLImageElement) {
    const { naturalWidth: width, naturalHeight: height } = element;
    if (width === 0 || height === 0) return;
    const scale = Math.min(maxWidth / width, maxHeight / height);
    const finalWidth = scale * width;
    const finalHeight = scale * height;
    setDimensions({
      width: largeImage ? finalWidth + 50 : finalWidth,
      height: largeImage ? finalHeight + 50 : finalHeight,
    });
  }

  useEffect(() => {
    const imgEl = document.querySelector(`.product-image-${index}`) as HTMLImageElement;
    if (imgEl) onImageLoad(imgEl);
  }, []);

  const isFluidWidth = (dimensions.width === 0 && dimensions.height === 0)
    || isMobile
    || !largeImage
    || !isWebpImage;

  const imgStyle = isFluidWidth
    ? { width: "100%", height: "auto" }
    : { width: dimensions.width, height: dimensions.height };

  const imgClassName = classNames(
    `product-image-${index}`,
    styles.product_image,
    smallImageClass,
    { [styles.jpg_product_image]: !isWebpImage },
  );

  return (
    <MagnifyImage
      src={canUseWebp ? relativeImgSrc : convertedImageSrc}
      zoomSrc={zoomSrc}
      alt={getImageAlt(imageObj.img, imageTitle)}
      imgStyle={imgStyle}
      imgClassName={imgClassName}
      placement={zoomImagePlacement === "beside" ? "beside" : "over"}
      portalEl={portalEl}
      zoomScale={largeImage ? 2 : 3}
      magnifiedContainerStyle={magnifiedContainerStyle}
      onLoad={onImageLoad}
      onZoomError={() => setZoomSrc(getRelativeImagePath(`/img/ztm/${imageObj.zoomImg}`))}
    />
  );
}

export default ProductImage;
